/**
 * The fill journal: real SPX credit spreads (and example trades to avoid), each set against the market at the
 * time it was placed: the quoted natural and mid credit, how the fill compared, the SPX level and the short
 * strike's distance at entry, the official settlement and the net result, and what Sentinel recorded about the
 * session. Data: /v1/fills (backend/fill_journal.py). A record, not a signal.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { QueryBoundary, retryTransient } from "@/lib/queryState";
import { Badge, Button, PageHead, Panel, Select, StatCell, TextField, fmt, etAndLocal, localOf } from "@/sentinel";

const isNum = (v) => v !== null && v !== undefined && v !== "" && !Number.isNaN(Number(v));
const pts = (v) => (isNum(v) ? Number(v).toFixed(2) : "—");
const spts = (v) => (isNum(v) ? `${Number(v) >= 0 ? "+" : "−"}${Math.abs(Number(v)).toFixed(2)}` : "—");
const usd = (v) => (isNum(v) ? `${Number(v) < 0 ? "−" : ""}$${Math.abs(Number(v)).toLocaleString("en-US", { maximumFractionDigits: 0 })}` : "—");
const pct2 = (v) => (isNum(v) ? `${(Number(v) * 100).toFixed(2)}%` : "—");
const level = (v) => (isNum(v) ? fmt(Number(v), "price") : "—");
const errText = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";

const BLANK = { day: "", entry_time: "10:00", side: "put", root: "SPXW", short: "", long: "", contracts: "1", credit: "", exit: "expired",
  exit_time: "", exit_debit: "", fees: "", settlement_override: "", kind: "real", notes: "" };

function EntryForm({ onSaved }) {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f };
      ["short", "long", "credit", "exit_debit", "fees", "settlement_override"].forEach((k) => { if (body[k] === "") delete body[k]; });
      const r = await api.addFill(body);
      toast.success(`Saved ${r.day}: ${isNum(r.result?.net) ? `net ${usd(r.result.net)}` : "result pending"}.`);
      setF({ ...BLANK, day: f.day });
      onSaved();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Add a trade" icon="Plus" data-testid="fill-form">
      <div className="sn-stack" style={{ gap: "var(--space-3)" }}>
        <p className="sn-small sn-muted" style={{ margin: 0 }}>
          One line per spread. Times are New York time; the local equivalent shows under each time field. For a partial close, add two lines: the contracts closed early and the
          contracts held to expiry. Sentinel looks up the quotes at your entry time and the official settlement.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <TextField label="Day" type="date" value={f.day} onChange={set("day")} data-testid="fill-day" />
          <TextField label="Entry time (ET)" type="time" value={f.entry_time} onChange={set("entry_time")} hint={f.entry_time ? `= ${localOf(f.entry_time, { day: f.day })}` : undefined} data-testid="fill-time" />
          <Select label="Spread" value={f.side} onChange={set("side")} options={[{ value: "put", label: "Bull put" }, { value: "call", label: "Bear call" }]} />
          <Select label="Kind" value={f.kind} onChange={set("kind")} options={[{ value: "real", label: "Real trade" }, { value: "example", label: "Example to avoid" }]} />
          <Select label="Contract" value={f.root} onChange={set("root")} options={[{ value: "SPXW", label: "SPXW (weekly, PM-settled)" }, { value: "SPX", label: "SPX monthly (AM-settled)" }]} />
          <TextField label="Short strike" numeric inputMode="decimal" value={f.short} onChange={set("short")} data-testid="fill-short" />
          <TextField label="Long strike" numeric inputMode="decimal" value={f.long} onChange={set("long")} data-testid="fill-long" />
          <TextField label="Contracts" numeric inputMode="numeric" value={f.contracts} onChange={set("contracts")} />
          <TextField label="Credit received (points)" numeric inputMode="decimal" value={f.credit} onChange={set("credit")} data-testid="fill-credit" />
          <Select label="How it ended" value={f.exit} onChange={set("exit")} options={[{ value: "expired", label: "Held to expiry" }, { value: "closed", label: "Closed early" }]} />
          {f.exit === "closed" && <TextField label="Debit paid to close (points)" numeric inputMode="decimal" value={f.exit_debit} onChange={set("exit_debit")} />}
          {f.exit === "closed" && <TextField label="Close time (ET)" type="time" value={f.exit_time} onChange={set("exit_time")} hint={f.exit_time ? `= ${localOf(f.exit_time, { day: f.day })}` : undefined} />}
          <TextField label="Total fees ($, optional)" numeric inputMode="decimal" value={f.fees} onChange={set("fees")} hint="blank: $1.50 a contract per leg" />
          {f.exit === "expired" && <TextField label="Settlement (only for AM-settled SPX)" numeric inputMode="decimal" value={f.settlement_override} onChange={set("settlement_override")} />}
        </div>
        <TextField label="Notes" value={f.notes} onChange={set("notes")} />
        <div className="sn-row"><Button variant="primary" icon="Plus" onClick={save} loading={busy} data-testid="fill-save">Save trade</Button></div>
      </div>
    </Panel>
  );
}

function Summary({ s }) {
  const r = s?.real || {};
  const x = s?.example || {};
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="fill-summary">
      <StatCell label="Real trades" value={String(r.entries ?? 0)} sub={r.with_result ? `${r.won} of ${r.with_result} made money` : null} />
      <StatCell label="Real trades, net" value={usd(r.total_net)} sub={isNum(r.mean_net) ? `${usd(r.mean_net)} a trade · worst ${usd(r.worst)}` : null} />
      <StatCell label="Fill against the natural credit" value={isNum(r.fill_vs_natural_mean) ? `${spts(r.fill_vs_natural_mean)} pts` : "—"}
        sub={r.fills_compared ? `average over ${r.fills_compared} fills; + means better than bid/ask` : "needs quotes at the entry time"} />
      <StatCell label="Examples to avoid" value={String(x.entries ?? 0)} sub={isNum(x.total_net) ? `net ${usd(x.total_net)}` : null} />
    </div>
  );
}

function Row({ e, onDelete }) {
  const m = e.market || {};
  const c = e.context || {};
  const r = e.result || {};
  const p = c.paper_bull_put;
  return (
    <tr data-testid={`fill-row-${e.id}`}>
      <td>{e.day}<div className="sn-faint sn-small">{etAndLocal(e.entry_time, { day: e.day })}{e.kind === "example" ? " · example" : ""}</div></td>
      <td>{e.side === "put" ? "Bull put" : "Bear call"} {e.short}/{e.long}<div className="sn-faint sn-small">{e.contracts} × · {e.exit === "closed" ? `closed ${e.exit_time ? etAndLocal(e.exit_time, { day: e.day }) : ""} at ${pts(e.exit_debit)}` : "held"}</div></td>
      <td className="num">{pts(e.credit)}<div className="sn-faint sn-small">natural {pts(m.natural_credit)} · mid {pts(m.mid_credit)}</div></td>
      <td className="num">{spts(m.fill_vs_natural)}{m.late_quote ? <div className="sn-faint sn-small">late quote</div> : null}</td>
      <td className="num">{level(m.spx_at_entry)}<div className="sn-faint sn-small">{isNum(m.short_distance) ? `short ${pct2(Math.abs(m.short_distance))} ${m.short_distance < 0 ? "below" : "above"}` : ""}</div></td>
      <td className="num">{level(e.settlement)}{e.settle_near_strike ? <div className="sn-small" style={{ color: "var(--price-down)" }}>near the strike</div> : null}</td>
      <td className="num" style={{ color: isNum(r.net) && r.net < 0 ? "var(--price-down)" : undefined }}>{usd(r.net)}<div className="sn-faint sn-small">fees {usd(r.fees)}</div></td>
      <td className="sn-small">
        {c.quiet_filter ? `filter ${c.quiet_filter === "KEPT" ? "keeps" : "excludes"}` : "—"}{c.fomc_day ? " · FOMC decision" : c.fomc_other?.length ? ` · ${c.fomc_other[0].replace(/ \([^)]*\)$/, "")}` : ""}
        {c.releases_before?.length ? ` · releases up to 10:00: ${c.releases_before.slice(0, 2).join(", ")}` : ""}
        {isNum(c.max_fall) ? <div className="sn-faint">after 10:00: {pct2(c.max_rise)} / {pct2(c.max_fall)}</div> : null}
        {p ? <div className="sn-faint">paper bull put: {p.status === "settled" ? usd(p.net) : p.status}</div> : null}
      </td>
      <td><Button size="sm" variant="ghost" icon="X" aria-label="Delete" onClick={() => onDelete(e)} /></td>
    </tr>
  );
}

export default function FillJournal() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["fills"], queryFn: api.getFills, retry: retryTransient });
  const refresh = () => qc.invalidateQueries({ queryKey: ["fills"] });
  const del = async (e) => {
    if (!window.confirm(`Delete the ${e.day} ${e.short}/${e.long} entry?`)) return;
    try { await api.deleteFill(e.id); refresh(); } catch (x) { toast.error(errText(x)); }
  };
  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }}>
      <PageHead crumb={["Evidence"]} title={<span data-testid="fills-title">Fill journal</span>}
        context="Real trades against the market at the time: quotes, settlement and what Sentinel saw that day · a record, not a signal" />
      <QueryBoundary query={q} loading={3} what="the fill journal" errorTestId="fills-error" isEmpty={() => false}>
        {(data) => (
          <div className="sn-stack" style={{ gap: "var(--space-5)" }}>
            <Summary s={data.summary} />
            <Panel title="Trades" icon="ClipboardCheck" meta={<Badge>{(data.entries || []).length} entries</Badge>}>
              <div className="sn-table-wrap">
                <table className="sn-table" aria-label="Fill journal">
                  <thead><tr><th>Day</th><th>Spread</th><th className="r">Credit</th><th className="r">Fill vs natural</th><th className="r">SPX at entry</th>
                    <th className="r">Settlement</th><th className="r">Net</th><th>What Sentinel saw</th><th /></tr></thead>
                  <tbody>{(data.entries || []).map((e) => <Row key={e.id} e={e} onDelete={del} />)}</tbody>
                </table>
              </div>
            </Panel>
          </div>
        )}
      </QueryBoundary>
      <EntryForm onSaved={refresh} />
    </div>
  );
}
